/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Extracts a human-readable message from an unknown API error shape.
 *
 * Covers the shapes the Housien API and its clients produce: axios-style
 * `response.data.detail`, generated-client `body.detail`, RFC-7807
 * `problem+json` (`detail`/`title`), and plain `Error.message`.
 *
 * @param err - The thrown value, of unknown shape.
 * @param fallback - Message returned when nothing usable is found.
 * @returns The first usable message string, or `fallback`.
 */
export function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (!err || typeof err !== 'object') return fallback;
  const candidates: Array<unknown> = [
    (err as { response?: { data?: { detail?: string } } }).response?.data?.detail,
    (err as { response?: { data?: { error?: string } } }).response?.data?.error,
    (err as { body?: { detail?: string } }).body?.detail,
    (err as { detail?: unknown }).detail,
    (err as { title?: unknown }).title,
    (err as { message?: unknown }).message,
  ];
  const found = candidates.find((c) => typeof c === 'string' && c);
  return (found as string) ?? fallback;
}
